<template>
  <WebLayout
    title="Sistema Administrativo"
    description="Administra clientes, productos, inventarios, compras y ventas en un solo lugar. Optimiza tus procesos y toma mejores decisiones con Realgestión."
    url="https://realgestion.lat"
  >
    <Head :title="`Realgestión - El sistema que tu empresa necesita`" />

    <!-- ═══════════════ HERO SECTION ═══════════════ -->
    <section class="relative min-h-screen flex items-center justify-center overflow-hidden hero-section">
      <!-- Background decorations -->
      <div class="absolute inset-0 bg-warm-cream">
        <!-- Gradient orbs -->
        <div class="absolute orb-1 rounded-full bg-orange-gradient animate-float-slow" />
        <div class="absolute orb-2 rounded-full bg-amber-gradient animate-float-slower" />
        <div class="absolute orb-3 rounded-full bg-rose-gradient" />
        <!-- Dot pattern -->
        <div class="absolute inset-0 dot-pattern" />
      </div>

      <!-- Floating pills (Nominal style) -->
      <div class="hidden lg:block absolute inset-0 pointer-events-none z-10 overflow-hidden">
        <!-- Pill 1: Ventas (Top-Left) -->
        <div class="absolute pill-pos-1 floating-pill pill-delay-1 pointer-events-auto">
          <div class="flex items-center gap-3 px-4 py-2.5 bg-white border border-gray-200 rounded-2xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300">
            <div class="w-8 h-8 rounded-xl bg-orange-50 border border-orange-100 flex items-center justify-center flex-shrink-0">
              <i class="fas fa-chart-line text-orange-500 text-xs" />
            </div>
            <div class="text-left">
              <div class="text-xs font-bold text-gray-900 leading-tight">Ventas POS</div>
              <div class="text-2xs text-gray-400 font-medium">Facturación en vivo</div>
            </div>
          </div>
        </div>

        <!-- Pill 2: Condominios (Top-Right) -->
        <div class="absolute pill-pos-2 floating-pill pill-delay-2 pointer-events-auto">
          <div class="flex items-center gap-3 px-4 py-2.5 bg-white border border-gray-200 rounded-2xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300">
            <div class="w-8 h-8 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center flex-shrink-0">
              <i class="fas fa-city text-emerald-500 text-xs" />
            </div>
            <div class="text-left">
              <div class="text-xs font-bold text-gray-900 leading-tight">Condominios</div>
              <div class="text-2xs text-gray-400 font-medium">Portal Propietarios</div>
            </div>
          </div>
        </div>

        <!-- Pill 3: Clientes (Middle-Left) -->
        <div class="absolute pill-pos-3 floating-pill pill-delay-3 pointer-events-auto">
          <div class="flex items-center gap-3 px-4 py-2.5 bg-white border border-gray-200 rounded-2xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300">
            <div class="w-8 h-8 rounded-xl bg-amber-50 border border-amber-100 flex items-center justify-center flex-shrink-0">
              <i class="fas fa-users text-amber-500 text-xs" />
            </div>
            <div class="text-left">
              <div class="text-xs font-bold text-gray-900 leading-tight">Clientes</div>
              <div class="text-2xs text-gray-400 font-medium">Créditos y saldos</div>
            </div>
          </div>
        </div>

        <!-- Pill 4: Inventarios (Middle-Right) -->
        <div class="absolute pill-pos-4 floating-pill pill-delay-4 pointer-events-auto">
          <div class="flex items-center gap-3 px-4 py-2.5 bg-white border border-gray-200 rounded-2xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300">
            <div class="w-8 h-8 rounded-xl bg-purple-50 border border-purple-100 flex items-center justify-center flex-shrink-0">
              <i class="fas fa-warehouse text-purple-500 text-xs" />
            </div>
            <div class="text-left">
              <div class="text-xs font-bold text-gray-900 leading-tight">Inventario</div>
              <div class="text-2xs text-gray-400 font-medium">Multidepósito</div>
            </div>
          </div>
        </div>

        <!-- Pill 5: Ecommerce (Lower-Left) -->
        <div class="absolute pill-pos-5 floating-pill pill-delay-5 pointer-events-auto">
          <div class="flex items-center gap-3 px-4 py-2.5 bg-white border border-gray-200 rounded-2xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300">
            <div class="w-8 h-8 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center flex-shrink-0">
              <i class="fas fa-store text-blue-500 text-xs" />
            </div>
            <div class="text-left">
              <div class="text-xs font-bold text-gray-900 leading-tight">Ecommerce</div>
              <div class="text-2xs text-gray-400 font-medium">Catálogo WhatsApp</div>
            </div>
          </div>
        </div>

        <!-- Pill 6: Cobranzas (Lower-Right) -->
        <div class="absolute pill-pos-6 floating-pill pill-delay-6 pointer-events-auto">
          <div class="flex items-center gap-3 px-4 py-2.5 bg-white border border-gray-200 rounded-2xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300">
            <div class="w-8 h-8 rounded-xl bg-teal-50 border border-teal-100 flex items-center justify-center flex-shrink-0">
              <i class="fas fa-file-invoice-dollar text-teal-500 text-xs" />
            </div>
            <div class="text-left">
              <div class="text-xs font-bold text-gray-900 leading-tight">Cobranzas</div>
              <div class="text-2xs text-gray-400 font-medium">Alícuotas al día</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Content -->
      <div class="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-20">
        <!-- Badge -->
        <div class="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-full shadow-sm mb-8 animate-fade-in-up">
          <span class="flex h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
          <span class="text-sm font-medium text-gray-600">La nueva generación en gestión empresarial</span>
        </div>

        <!-- Headline -->
        <h1 class="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight hero-headline mb-8 animate-fade-in-up animation-delay-100">
          <span class="text-gray-900">Gestiona tu empresa</span><br />
          <span class="gradient-text">con inteligencia.</span>
        </h1>

        <!-- Subtitle -->
        <p class="text-lg sm:text-xl text-gray-500 max-w-2xl mx-auto leading-relaxed mb-12 animate-fade-in-up animation-delay-200 font-light">
          Control total de clientes, productos, inventarios, ventas y compras.
          <strong class="text-gray-700 font-medium">Ecommerce integrado</strong> y 
          <strong class="text-gray-700 font-medium">gestión de condominios</strong> en una sola plataforma.
        </p>

        <!-- CTAs -->
        <div class="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16 animate-fade-in-up animation-delay-300">
          <Link href="/register" class="group w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-4 text-base font-semibold text-white bg-gray-900 hover:bg-gray-800 rounded-full transition-all duration-300 hover:shadow-xl hover:shadow-gray-900/20 hover:-translate-y-0.5">
            Comenzar gratis
            <svg class="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
          </Link>
          <a href="#soluciones" class="w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-4 text-base font-medium text-gray-600 bg-white hover:bg-gray-50 border border-gray-200 rounded-full transition-all duration-300 hover:shadow-md hover:-translate-y-0.5">
            Explorar soluciones
          </a>
        </div>

        <!-- Stats Row -->
        <div class="grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-2xl mx-auto animate-fade-in-up animation-delay-400">
          <div v-for="stat in stats" :key="stat.label" class="text-center">
            <div class="text-2xl sm:text-3xl font-bold text-gray-900 mb-1 font-display">{{ stat.value }}</div>
            <div class="text-xs text-gray-400 font-medium uppercase tracking-wide">{{ stat.label }}</div>
          </div>
        </div>
      </div>

      <!-- Scroll indicator -->
      <div class="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <div class="w-6 h-10 border-2 border-gray-300 rounded-full flex items-start justify-center p-1">
          <div class="w-1.5 h-2.5 bg-gray-400 rounded-full animate-scroll-down" />
        </div>
      </div>
    </section>

    <!-- ═══════════════ SOLUTIONS OVERVIEW (Bento Grid) ═══════════════ -->
    <section id="soluciones" class="py-24 bg-white relative">
      <div class="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />

      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <!-- Section Header -->
        <div class="text-center max-w-3xl mx-auto mb-16">
          <div class="inline-flex items-center gap-2 px-3 py-1 bg-orange-50 border border-orange-100 rounded-full text-xs font-semibold text-orange-600 uppercase tracking-widest mb-6">
            Soluciones
          </div>
          <h2 class="text-4xl sm:text-5xl font-bold text-gray-900 tracking-tight mb-6">
            Una plataforma,<br />
            <span class="text-gray-400">tres soluciones.</span>
          </h2>
          <p class="text-lg text-gray-500 font-light leading-relaxed">
            Ya sea que administres una empresa, un condominio o una tienda online, Realgestión se adapta a tus necesidades.
          </p>
        </div>

        <!-- Bento Grid -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <a
            v-for="solution in solutions" :key="solution.key" :href="solution.href"
            class="group relative bg-white border border-gray-200 rounded-3xl p-8 lg:p-10 transition-all duration-500 hover:-translate-y-2 card-hover-shadow overflow-hidden"
          >
            <!-- Hover gradient -->
            <div
              class="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none rounded-3xl"
              :class="{
                'bg-gradient-to-br from-orange-50 to-transparent': solution.color === 'orange',
                'bg-gradient-to-br from-emerald-50 to-transparent': solution.color === 'emerald',
                'bg-gradient-to-br from-blue-50 to-transparent': solution.color === 'blue',
              }"
            />

            <div class="relative z-10">
              <!-- Icon -->
              <div
                class="w-14 h-14 rounded-2xl flex items-center justify-center mb-6 transition-colors duration-300"
                :class="{
                  'bg-orange-50 border border-orange-100 group-hover:bg-orange-500 group-hover:border-orange-400': solution.color === 'orange',
                  'bg-emerald-50 border border-emerald-100 group-hover:bg-emerald-500 group-hover:border-emerald-400': solution.color === 'emerald',
                  'bg-blue-50 border border-blue-100 group-hover:bg-blue-500 group-hover:border-blue-400': solution.color === 'blue',
                }"
              >
                <i
                  :class="[
                    solution.icon,
                    'text-xl transition-colors duration-300 group-hover:text-white',
                    solution.color === 'orange' ? 'text-orange-500' : '',
                    solution.color === 'emerald' ? 'text-emerald-500' : '',
                    solution.color === 'blue' ? 'text-blue-500' : '',
                  ]"
                />
              </div>

              <!-- Text -->
              <div
                class="text-xs font-semibold uppercase tracking-widest mb-2"
                :class="{
                  'text-orange-500': solution.color === 'orange',
                  'text-emerald-500': solution.color === 'emerald',
                  'text-blue-500': solution.color === 'blue',
                }"
              >
                {{ solution.subtitle }}
              </div>
              <h3 class="text-2xl font-bold text-gray-900 mb-3 font-display">{{ solution.title }}</h3>
              <p class="text-gray-500 leading-relaxed text-sm">{{ solution.description }}</p>

              <!-- Arrow -->
              <div
                class="mt-6 flex items-center gap-2 text-sm font-semibold opacity-0 group-hover:opacity-100 transform -translate-x-2 group-hover:translate-x-0 transition-all duration-300"
                :class="{
                  'text-orange-600': solution.color === 'orange',
                  'text-emerald-600': solution.color === 'emerald',
                  'text-blue-600': solution.color === 'blue',
                }"
              >
                Explorar
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
              </div>
            </div>
          </a>
        </div>
      </div>
    </section>

    <!-- ═══════════════ ADMINISTRATIVO FEATURES ═══════════════ -->
    <section id="administrativo" class="py-24 bg-warm-cream relative">
      <div class="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />

      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="grid lg:grid-cols-12 gap-12 lg:gap-16">
          <!-- Left: Header + Tabs -->
          <div class="lg:col-span-4">
            <div class="lg:sticky lg:top-24">
              <div class="inline-flex items-center gap-2 px-3 py-1 bg-orange-50 border border-orange-100 rounded-full text-xs font-semibold text-orange-600 uppercase tracking-widest mb-6">
                <i class="fas fa-building text-2xs" /> Administrativo
              </div>
              <h2 class="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight mb-4 font-display">
                Todo lo que necesitas para tu empresa.
              </h2>
              <p class="text-gray-500 leading-relaxed mb-8">
                Desde la gestión de clientes hasta el control de gastos. Un ecosistema completo para que te enfoques en crecer.
              </p>

              <!-- Feature Tabs (Grassfeld style pills) -->
              <div class="flex flex-wrap gap-2">
                <button
                  v-for="(feature, index) in adminFeatures" :key="feature.key"
                  class="px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 border"
                  :class="activeAdminTab === index 
                    ? 'bg-orange-500 text-white border-orange-500 shadow-lg shadow-orange-500/20' 
                    : 'bg-white text-gray-600 border-gray-200 hover:border-orange-200 hover:text-orange-600'"
                  @click="activeAdminTab = index"
                >
                  {{ feature.title.replace('Gestión de ', '').replace('Mi ', '') }}
                </button>
              </div>
            </div>
          </div>

          <!-- Right: Active Feature Detail -->
          <div class="lg:col-span-8">
            <transition name="feature-fade" mode="out-in">
              <div :key="activeAdminTab" class="bg-white rounded-3xl border border-gray-200 p-8 lg:p-10 shadow-sm">
                <!-- Feature Header -->
                <div class="flex items-start gap-4 mb-8">
                  <div class="w-14 h-14 rounded-2xl bg-orange-50 border border-orange-100 flex items-center justify-center flex-shrink-0">
                    <i :class="adminFeatures[activeAdminTab].icon + ' text-orange-500 text-xl'" />
                  </div>
                  <div>
                    <h3 class="text-2xl font-bold text-gray-900 mb-2">{{ adminFeatures[activeAdminTab].title }}</h3>
                    <p class="text-gray-500 leading-relaxed">{{ adminFeatures[activeAdminTab].description }}</p>
                  </div>
                </div>

                <!-- Highlights Grid -->
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div
                    v-for="highlight in adminFeatures[activeAdminTab].highlights" :key="highlight"
                    class="flex items-center gap-3 px-4 py-3 bg-orange-50/50 border border-orange-100/50 rounded-xl"
                  >
                    <div class="w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                      <svg class="w-3.5 h-3.5 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" /></svg>
                    </div>
                    <span class="text-sm font-medium text-gray-700">{{ highlight }}</span>
                  </div>
                </div>

                <!-- CTA -->
                <div class="mt-8 flex items-center gap-4">
                  <Link href="/register" class="inline-flex items-center gap-2 px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold rounded-full transition-all duration-300 hover:shadow-lg hover:shadow-orange-500/20 hover:-translate-y-px">
                    Probar ahora
                    <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                  </Link>
                </div>
              </div>
            </transition>
          </div>
        </div>
      </div>
    </section>

    <!-- ═══════════════ CONDOMINIOS SECTION ═══════════════ -->
    <section id="condominios" class="py-24 bg-white relative">
      <div class="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />

      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <!-- Section Header -->
        <div class="text-center max-w-3xl mx-auto mb-16">
          <div class="inline-flex items-center gap-2 px-3 py-1 bg-emerald-50 border border-emerald-100 rounded-full text-xs font-semibold text-emerald-600 uppercase tracking-widest mb-6">
            <i class="fas fa-city text-2xs" /> Condominios
          </div>
          <h2 class="text-4xl sm:text-5xl font-bold text-gray-900 tracking-tight mb-6 font-display">
            Administración de comunidades,<br />
            <span class="text-emerald-500">simplificada.</span>
          </h2>
          <p class="text-lg text-gray-500 font-light leading-relaxed">
            Gestiona propietarios, cobranzas y alícuotas con transparencia total. Incluye portal de autogestión para cada propietario.
          </p>
        </div>

        <!-- Features Grid -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div
            v-for="(feature, index) in condoFeatures" :key="feature.key"
            class="group relative bg-white border border-gray-200 rounded-3xl p-8 transition-all duration-500 hover:-translate-y-2 card-hover-shadow-emerald overflow-hidden"
            :class="{'lg:col-span-2': index === condoFeatures.length - 1 && condoFeatures.length % 3 !== 0}"
          >
            <!-- Hover bg -->
            <div class="absolute inset-0 bg-gradient-to-br from-emerald-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none rounded-3xl" />

            <div class="relative z-10">
              <div class="w-12 h-12 rounded-2xl bg-emerald-50 border border-emerald-100 flex items-center justify-center mb-6 group-hover:bg-emerald-500 group-hover:border-emerald-400 transition-colors duration-300">
                <i :class="feature.icon + ' text-emerald-500 group-hover:text-white transition-colors duration-300 text-lg'" />
              </div>
              <h3 class="text-xl font-bold text-gray-900 mb-3">{{ feature.title }}</h3>
              <p class="text-gray-500 leading-relaxed text-sm mb-5">{{ feature.description }}</p>

              <!-- Mini highlights -->
              <div class="flex flex-wrap gap-2">
                <span v-for="h in feature.highlights" :key="h" class="inline-flex items-center px-2.5 py-1 bg-emerald-50/80 border border-emerald-100/60 rounded-lg text-xs font-medium text-emerald-700">
                  {{ h }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- CTA Cotizador -->
        <div class="mt-14 max-w-2xl mx-auto text-center">
          <div class="p-8 sm:p-10 bg-gradient-to-b from-emerald-50/70 via-emerald-50/30 to-white border border-emerald-100 rounded-3xl shadow-sm">
            <div class="w-14 h-14 rounded-2xl bg-emerald-500 text-white flex items-center justify-center mx-auto mb-4 shadow-lg shadow-emerald-500/25">
              <i class="fas fa-calculator text-xl" />
            </div>
            <h3 class="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 font-display">¿Cuántos edificios deseas administrar?</h3>
            <p class="text-gray-500 text-sm sm:text-base mb-6 max-w-lg mx-auto leading-relaxed">
              Cotiza tu inversión en tiempo real: <strong class="text-emerald-600 font-semibold">0,70$ por edificio</strong> (mínimo 30$ mensual) y consulta directamente con un asesor por WhatsApp.
            </p>
            <button 
              type="button"
              class="inline-flex items-center justify-center gap-2.5 px-8 py-4 bg-emerald-500 hover:bg-emerald-600 text-white text-base font-semibold rounded-full transition-all duration-300 hover:shadow-xl hover:shadow-emerald-500/25 hover:-translate-y-0.5 cursor-pointer" 
              @click="openCondoModal"
            >
              <i class="fas fa-calculator text-sm" />
              Cotizar ahora para mis edificios
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
            </button>
            <p class="text-2xs text-gray-400 mt-3 font-medium">Cotización inmediata · Sin compromiso · Asesoría personalizada</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ═══════════════ ECOMMERCE SECTION ═══════════════ -->
    <section id="ecommerce" class="relative py-24 overflow-hidden">
      <!-- Gradient Background -->
      <div class="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900" />
      <div class="absolute inset-0 radial-blue-glow" />
      <div class="absolute inset-0 radial-orange-ecom" />
      <!-- Dot pattern -->
      <div class="absolute inset-0 dot-pattern-dark" />

      <div class="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="grid lg:grid-cols-2 gap-16 items-center">
          <!-- Text -->
          <div>
            <div class="inline-flex items-center gap-2 px-3 py-1 bg-blue-500 bg-opacity-20 border border-blue-400 border-opacity-30 rounded-full text-xs font-semibold text-blue-300 uppercase tracking-widest mb-6">
              <i class="fas fa-store text-2xs" /> Ecommerce
            </div>
            <h2 class="text-4xl sm:text-5xl font-bold text-white tracking-tight mb-6 leading-tight font-display">
              Tu tienda online,<br />
              <span class="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">lista en segundos.</span>
            </h2>
            <p class="text-lg text-gray-200 leading-relaxed mb-8 font-light">
              Comparte un enlace único con tus clientes para que vean tus productos, armen su pedido y te lo envíen directo por 
              <strong class="text-white font-semibold">WhatsApp</strong>. Sin comisiones, sin tecnicismos.
            </p>

            <!-- Steps with high contrast white text -->
            <div class="grid grid-cols-3 gap-4 mb-10">
              <div v-for="(step, i) in ecommerceSteps" :key="i" class="text-center">
                <div class="w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-3 text-white font-bold text-sm" style="background: rgba(255,255,255,0.15); border: 1px solid rgba(255,255,255,0.35);">
                  {{ i + 1 }}
                </div>
                <p class="text-white text-xs font-semibold leading-snug">{{ step }}</p>
              </div>
            </div>

            <!-- Features mini -->
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
              <div v-for="feat in ecommerceFeatures" :key="feat.title" class="flex items-start gap-3 p-4 rounded-2xl backdrop-blur-sm" style="background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.18);">
                <div class="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style="background: rgba(59,130,246,0.3);">
                  <i :class="feat.icon + ' text-cyan-300 text-sm'" />
                </div>
                <div>
                  <div class="text-sm font-bold text-white mb-0.5">{{ feat.title }}</div>
                  <div class="text-xs text-gray-200 leading-tight">{{ feat.description }}</div>
                </div>
              </div>
            </div>

            <!-- CTAs -->
            <div class="flex flex-col sm:flex-row gap-4">
              <Link href="/register" class="group inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-gray-900 font-bold text-base rounded-full shadow-xl hover:shadow-2xl hover:bg-gray-100 hover:-translate-y-0.5 transition-all duration-300">
                <i class="fas fa-rocket group-hover:rotate-12 transition-transform duration-300 text-sm text-blue-600" />
                Crear mi catálogo gratis
              </Link>
              <a href="https://dondeeglindo.realgestion.lat" target="_blank" class="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-cyan-400 text-white hover:text-cyan-200 font-semibold text-base rounded-full transition-all duration-300 backdrop-blur-sm hover:bg-white hover:bg-opacity-10" style="background: rgba(255,255,255,0.08);">
                <i class="fas fa-external-link-alt text-sm text-cyan-300" />
                Ver demo en vivo
              </a>
            </div>
          </div>

          <!-- Phone Mockup -->
          <div class="relative flex items-center justify-center">
            <!-- Glow -->
            <div class="absolute inset-0 bg-blue-500 bg-opacity-10 rounded-3xl blur-3xl scale-75" />
            
            <!-- Phone frame -->
            <div class="relative phone-mockup-wrapper w-full mx-auto">
              <div class="bg-white rounded-3xl shadow-2xl overflow-hidden border-4 border-white border-opacity-80 phone-aspect">
                <!-- Status bar -->
                <div class="bg-gradient-to-r from-blue-500 to-blue-600 h-8 flex items-center justify-between px-4">
                  <span class="text-white text-xs font-medium">Mi Tienda 🛍️</span>
                  <div class="flex items-center gap-1">
                    <i class="fas fa-wifi text-white text-2xs" />
                    <i class="fas fa-battery-three-quarters text-white text-2xs ml-1" />
                  </div>
                </div>
                <!-- Search bar mock -->
                <div class="px-3 py-2 bg-blue-50 border-b border-blue-100">
                  <div class="bg-white rounded-full px-3 py-1.5 flex items-center gap-2 shadow-sm border border-gray-100">
                    <i class="fas fa-search text-gray-400 text-2xs" />
                    <span class="text-gray-400 text-xs">Buscar producto...</span>
                  </div>
                </div>
                <!-- Category chips -->
                <div class="px-3 py-2 flex gap-1.5 overflow-hidden border-b border-gray-100">
                  <span class="px-2.5 py-1 bg-blue-500 text-white rounded-full text-2xs font-medium whitespace-nowrap flex-shrink-0">Todos</span>
                  <span class="px-2.5 py-1 bg-gray-100 text-gray-600 rounded-full text-2xs whitespace-nowrap flex-shrink-0">Ropa</span>
                  <span class="px-2.5 py-1 bg-gray-100 text-gray-600 rounded-full text-2xs whitespace-nowrap flex-shrink-0">Calzado</span>
                  <span class="px-2.5 py-1 bg-gray-100 text-gray-600 rounded-full text-2xs whitespace-nowrap flex-shrink-0">Tech</span>
                </div>
                <!-- Product cards -->
                <div class="p-2.5 grid grid-cols-2 gap-2">
                  <div v-for="n in 6" :key="n" class="bg-white rounded-xl border border-gray-100 overflow-hidden shadow-sm">
                    <div class="h-12 flex items-center justify-center" :class="[n%3===0 ? 'bg-rose-50' : n%2===0 ? 'bg-blue-50' : 'bg-amber-50']">
                      <i class="fas fa-box text-base" :class="[n%3===0 ? 'text-rose-300' : n%2===0 ? 'text-blue-300' : 'text-amber-300']" />
                    </div>
                    <div class="p-1.5">
                      <p class="text-gray-700 text-2xs font-semibold truncate">Producto {{ n }}</p>
                      <div class="flex items-center justify-between mt-0.5">
                        <span class="text-blue-500 text-2xs font-bold">${{ (n * 12.5).toFixed(0) }}</span>
                        <button class="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                          <i class="fas fa-plus text-white" style="font-size: 6px;" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <!-- WhatsApp FAB -->
                <div class="absolute bottom-4 right-4 w-10 h-10 bg-green-500 rounded-full flex items-center justify-center shadow-lg shadow-green">
                  <i class="fab fa-whatsapp text-white text-lg" />
                </div>
              </div>

              <!-- Floating badges -->
              <div class="absolute -top-4 -right-8 bg-green-500 text-white px-3 py-1.5 rounded-full text-xs font-bold shadow-lg flex items-center gap-1 animate-bounce" style="animation-duration:2.5s">
                <i class="fab fa-whatsapp text-xs" /> ¡Pedido recibido!
              </div>
              <div class="absolute -bottom-2 -left-8 bg-white border border-gray-100 text-gray-700 px-3 py-1.5 rounded-full text-xs font-semibold shadow-lg">
                📦 5 productos en carrito
              </div>
            </div>
          </div>
        </div>

        <!-- ═══════════════ SHOWCASE WEB PERSONALIZADA (PORTAFOLIO) ═══════════════ -->
        <div class="mt-20 pt-16 border-t border-gray-800 relative">
          <div class="text-center max-w-3xl mx-auto mb-12">
            <div class="inline-flex items-center gap-2 px-3 py-1 bg-cyan-500 bg-opacity-20 border border-cyan-400 border-opacity-30 rounded-full text-xs font-semibold text-cyan-300 uppercase tracking-widest mb-4">
              <i class="fas fa-layer-group text-2xs" /> Desarrollo a Medida
            </div>
            <h3 class="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-4 font-display">
              ¿Buscas una web aún más personalizada?
            </h3>
            <p class="text-gray-200 text-base leading-relaxed font-light max-w-2xl mx-auto">
              Si tu empresa necesita una plataforma de comercio electrónico exclusiva, diseño corporativo a la medida o integraciones avanzadas, nuestro equipo la diseña y programa para ti. Conoce algunos proyectos desarrollados para nuestros clientes:
            </p>
          </div>

          <!-- Portfolio Grid (4 websites) -->
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <!-- Project 1: Distribuidora Gran Feria -->
            <a 
              href="https://distribuidoragranferia.com/" 
              target="_blank" 
              class="group relative rounded-2xl p-6 transition-all duration-300 hover:-translate-y-1.5 flex flex-col justify-between"
              style="background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.14);"
            >
              <div>
                <div class="flex items-center justify-between mb-4">
                  <span class="px-2.5 py-1 text-2xs font-semibold rounded-lg text-orange-300" style="background: rgba(249,115,22,0.2); border: 1px solid rgba(249,115,22,0.3);">
                    Mayorista / B2B
                  </span>
                  <i class="fas fa-external-link-alt text-gray-400 group-hover:text-cyan-400 text-xs transition-colors" />
                </div>
                <h4 class="text-lg font-bold text-white group-hover:text-cyan-300 transition-colors mb-2">
                  Gran Feria
                </h4>
                <p class="text-xs text-gray-300 leading-relaxed mb-4">
                  Tienda online de distribución y catálogo masivo de productos de consumo masivo.
                </p>
              </div>
              <div class="pt-3 border-t border-gray-700 flex items-center justify-between text-xs font-semibold text-cyan-400 group-hover:text-cyan-300">
                <span class="truncate">distribuidoragranferia.com</span>
                <i class="fas fa-arrow-right text-2xs group-hover:translate-x-1 transition-transform ml-2 flex-shrink-0" />
              </div>
            </a>

            <!-- Project 2: Kilates -->
            <a 
              href="https://kilates.com.ve/" 
              target="_blank" 
              class="group relative rounded-2xl p-6 transition-all duration-300 hover:-translate-y-1.5 flex flex-col justify-between"
              style="background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.14);"
            >
              <div>
                <div class="flex items-center justify-between mb-4">
                  <span class="px-2.5 py-1 text-2xs font-semibold rounded-lg text-amber-300" style="background: rgba(245,158,11,0.2); border: 1px solid rgba(245,158,11,0.3);">
                    Joyería & Retail
                  </span>
                  <i class="fas fa-external-link-alt text-gray-400 group-hover:text-cyan-400 text-xs transition-colors" />
                </div>
                <h4 class="text-lg font-bold text-white group-hover:text-cyan-300 transition-colors mb-2">
                  Kilates
                </h4>
                <p class="text-xs text-gray-300 leading-relaxed mb-4">
                  Boutique digital exclusiva de joyería fina con experiencia de compra de alta gama.
                </p>
              </div>
              <div class="pt-3 border-t border-gray-700 flex items-center justify-between text-xs font-semibold text-cyan-400 group-hover:text-cyan-300">
                <span class="truncate">kilates.com.ve</span>
                <i class="fas fa-arrow-right text-2xs group-hover:translate-x-1 transition-transform ml-2 flex-shrink-0" />
              </div>
            </a>

            <!-- Project 3: Ofilook -->
            <a 
              href="https://www.ofilook.com/" 
              target="_blank" 
              class="group relative rounded-2xl p-6 transition-all duration-300 hover:-translate-y-1.5 flex flex-col justify-between"
              style="background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.14);"
            >
              <div>
                <div class="flex items-center justify-between mb-4">
                  <span class="px-2.5 py-1 text-2xs font-semibold rounded-lg text-blue-300" style="background: rgba(59,130,246,0.2); border: 1px solid rgba(59,130,246,0.3);">
                    Mobiliario & Diseño
                  </span>
                  <i class="fas fa-external-link-alt text-gray-400 group-hover:text-cyan-400 text-xs transition-colors" />
                </div>
                <h4 class="text-lg font-bold text-white group-hover:text-cyan-300 transition-colors mb-2">
                  Ofilook
                </h4>
                <p class="text-xs text-gray-300 leading-relaxed mb-4">
                  Catálogo corporativo y soluciones ergonómicas integrales para espacios de oficina.
                </p>
              </div>
              <div class="pt-3 border-t border-gray-700 flex items-center justify-between text-xs font-semibold text-cyan-400 group-hover:text-cyan-300">
                <span class="truncate">ofilook.com</span>
                <i class="fas fa-arrow-right text-2xs group-hover:translate-x-1 transition-transform ml-2 flex-shrink-0" />
              </div>
            </a>

            <!-- Project 4: Grupo Mafica -->
            <a 
              href="https://www.grupomafica.com/" 
              target="_blank" 
              class="group relative rounded-2xl p-6 transition-all duration-300 hover:-translate-y-1.5 flex flex-col justify-between"
              style="background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.14);"
            >
              <div>
                <div class="flex items-center justify-between mb-4">
                  <span class="px-2.5 py-1 text-2xs font-semibold rounded-lg text-emerald-300" style="background: rgba(16,185,129,0.2); border: 1px solid rgba(16,185,129,0.3);">
                    Corporativo Industrial
                  </span>
                  <i class="fas fa-external-link-alt text-gray-400 group-hover:text-cyan-400 text-xs transition-colors" />
                </div>
                <h4 class="text-lg font-bold text-white group-hover:text-cyan-300 transition-colors mb-2">
                  Grupo Mafica
                </h4>
                <p class="text-xs text-gray-300 leading-relaxed mb-4">
                  Presencia corporativa y catálogo institucional para grupo empresarial industrial.
                </p>
              </div>
              <div class="pt-3 border-t border-gray-700 flex items-center justify-between text-xs font-semibold text-cyan-400 group-hover:text-cyan-300">
                <span class="truncate">grupomafica.com</span>
                <i class="fas fa-arrow-right text-2xs group-hover:translate-x-1 transition-transform ml-2 flex-shrink-0" />
              </div>
            </a>
          </div>

          <!-- Bottom CTA for Custom Web -->
          <div class="text-center">
            <a 
              href="https://wa.me/584244010195?text=¡Hola!%20Me%20interesa%20cotizar%20un%20sitio%20web%20o%20tienda%20online%20100%25%20personalizada%20a%20la%20medida%20para%20mi%20empresa." 
              target="_blank" 
              class="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-white font-bold text-sm sm:text-base rounded-full shadow-xl shadow-cyan-500 shadow-opacity-20 hover:shadow-cyan-500 hover:shadow-opacity-35 hover:-translate-y-0.5 transition-all duration-300"
            >
              <i class="fab fa-whatsapp text-lg" />
              Solicitar cotización de web personalizada
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
            </a>
          </div>
        </div>
      </div>
    </section>

    <!-- ═══════════════ STATS SECTION ═══════════════ -->
    <section class="py-20 bg-warm-cream relative">
      <div class="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />
      <div class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          <div v-for="stat in stats" :key="stat.label" class="text-center group">
            <div class="text-5xl sm:text-6xl font-bold text-gray-900 mb-3 font-display tracking-tight group-hover:text-orange-500 transition-colors duration-500">
              {{ stat.value }}
            </div>
            <div class="text-sm text-gray-400 font-medium">{{ stat.label }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- ═══════════════ TESTIMONIALS ═══════════════ -->
    <section id="testimonials" class="py-24 bg-white relative">
      <div class="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />

      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center max-w-3xl mx-auto mb-16">
          <div class="inline-flex items-center gap-2 px-3 py-1 bg-orange-50 border border-orange-100 rounded-full text-xs font-semibold text-orange-600 uppercase tracking-widest mb-6">
            Testimonios
          </div>
          <h2 class="text-4xl sm:text-5xl font-bold text-gray-900 tracking-tight mb-6 font-display">
            Lo que dicen<br />
            <span class="text-gray-400">nuestros clientes.</span>
          </h2>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div
            v-for="testimonial in testimonials" :key="testimonial.name"
            class="group bg-white border border-gray-200 rounded-3xl p-8 transition-all duration-500 hover:-translate-y-2 card-hover-shadow relative overflow-hidden"
          >
            <!-- Quote decoration -->
            <div class="absolute top-4 right-6 quote-icon-opacity pointer-events-none">
              <i class="fas fa-quote-right text-6xl text-gray-900" />
            </div>

            <!-- Stars -->
            <div class="flex items-center gap-1 mb-6">
              <i v-for="i in testimonial.rating" :key="i" class="fas fa-star text-orange-400 text-sm" />
            </div>

            <!-- Content -->
            <p class="text-gray-600 leading-relaxed mb-8 italic font-light">"{{ testimonial.content }}"</p>

            <!-- Author -->
            <div class="flex items-center gap-4 mt-auto pt-6 border-t border-gray-100">
              <div class="w-11 h-11 rounded-full bg-gradient-to-br from-orange-400 to-rose-400 flex items-center justify-center text-white font-bold text-sm">
                {{ testimonial.name.split(' ').map(n => n[0]).join('') }}
              </div>
              <div>
                <h4 class="text-sm font-bold text-gray-900">{{ testimonial.name }}</h4>
                <p class="text-xs text-gray-400">{{ testimonial.role }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ═══════════════ QUOTE / MISSION ═══════════════ -->
    <section class="py-24 bg-warm-cream relative overflow-hidden">
      <div class="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />
      <div class="absolute inset-0 radial-orange-glow" />

      <div class="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div class="w-16 h-16 rounded-full bg-white border border-gray-200 shadow-lg flex items-center justify-center mx-auto mb-10">
          <i class="fas fa-quote-right text-2xl text-orange-500" />
        </div>
        <blockquote class="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-8 font-display">
          <span class="text-gray-300">&ldquo;</span>Transforma los datos de tu negocio en 
          <span class="gradient-text">decisiones inteligentes</span>.<span class="text-gray-300">&rdquo;</span>
        </blockquote>
        <div class="w-12 h-1 bg-gradient-to-r from-orange-400 to-rose-400 rounded-full mx-auto mb-4" />
        <p class="text-sm font-semibold text-gray-400 uppercase tracking-widest">El equipo de Realgestión</p>
      </div>
    </section>

    <!-- ═══════════════ CONTACT / CTA SECTION ═══════════════ -->
    <section id="contacto" class="py-24 bg-white relative">
      <div class="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />

      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="grid lg:grid-cols-2 gap-16 items-center">
          <!-- Left: CTA Text -->
          <div>
            <div class="inline-flex items-center gap-2 px-3 py-1 bg-orange-50 border border-orange-100 rounded-full text-xs font-semibold text-orange-600 uppercase tracking-widest mb-6">
              Contacto
            </div>
            <h2 class="text-4xl sm:text-5xl font-bold text-gray-900 tracking-tight mb-6 font-display leading-tight">
              ¿Listo para<br />
              <span class="gradient-text">transformar tu negocio?</span>
            </h2>
            <p class="text-lg text-gray-500 leading-relaxed mb-8 font-light">
              Escríbenos y te ayudaremos a encontrar la solución perfecta para tu empresa. Sin compromisos, sin letra pequeña.
            </p>

            <!-- Contact Methods -->
            <div class="space-y-4">
              <a href="https://wa.me/584244010195" target="_blank" class="flex items-center gap-4 p-4 bg-green-50 border border-green-100 rounded-2xl hover:bg-green-100/50 transition-colors group">
                <div class="w-12 h-12 rounded-xl bg-green-500 flex items-center justify-center flex-shrink-0">
                  <i class="fab fa-whatsapp text-white text-xl" />
                </div>
                <div>
                  <div class="text-sm font-semibold text-gray-900">WhatsApp</div>
                  <div class="text-xs text-gray-500">Respuesta inmediata</div>
                </div>
                <svg class="w-5 h-5 text-gray-400 ml-auto group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
              </a>
              <a href="mailto:info@realgestion.lat" class="flex items-center gap-4 p-4 bg-blue-50 border border-blue-100 rounded-2xl hover:bg-blue-100/50 transition-colors group">
                <div class="w-12 h-12 rounded-xl bg-blue-500 flex items-center justify-center flex-shrink-0">
                  <i class="fas fa-envelope text-white text-lg" />
                </div>
                <div>
                  <div class="text-sm font-semibold text-gray-900">Email</div>
                  <div class="text-xs text-gray-500">info@realgestion.co</div>
                </div>
                <svg class="w-5 h-5 text-gray-400 ml-auto group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
              </a>
              <a href="https://www.instagram.com/realgestionve" target="_blank" class="flex items-center gap-4 p-4 bg-purple-50 border border-purple-100 rounded-2xl hover:bg-purple-100/50 transition-colors group">
                <div class="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                  <i class="fab fa-instagram text-white text-lg" />
                </div>
                <div>
                  <div class="text-sm font-semibold text-gray-900">Instagram</div>
                  <div class="text-xs text-gray-500">@realgestionve</div>
                </div>
                <svg class="w-5 h-5 text-gray-400 ml-auto group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
              </a>
            </div>
          </div>

          <!-- Right: CTA Card -->
          <div class="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-10 lg:p-12 text-center relative overflow-hidden">
            <!-- Decorative -->
            <div class="absolute inset-0 radial-orange-accent" />
            <div class="absolute inset-0 dot-pattern-light" />

            <div class="relative z-10">
              <div class="w-16 h-16 rounded-2xl bg-orange-500 flex items-center justify-center mx-auto mb-8 shadow-lg shadow-orange-500/30">
                <i class="fas fa-rocket text-white text-2xl" />
              </div>
              <h3 class="text-2xl sm:text-3xl font-bold text-white mb-4 font-display">Comienza hoy mismo</h3>
              <p class="text-gray-400 mb-8 leading-relaxed">
                Crea tu cuenta gratuita y descubre por qué decenas de empresas ya confían en Realgestión.
              </p>
              <Link href="/register" class="inline-flex items-center gap-2 px-8 py-4 bg-orange-500 hover:bg-orange-400 text-white font-bold text-base rounded-full transition-all duration-300 hover:shadow-xl hover:shadow-orange-500/30 hover:-translate-y-0.5">
                Crear cuenta gratis
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
              </Link>
              <p class="mt-6 text-xs text-gray-500">Sin tarjeta de crédito · Sin compromiso</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ═══════════════ MODAL COTIZADOR CONDOMINIOS ═══════════════ -->
    <transition
      enter-active-class="transition duration-300 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition duration-200 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div 
        v-if="showCondoModal" 
        class="fixed inset-0 z-50 overflow-y-auto flex items-center justify-center p-4 bg-gray-900 bg-opacity-60 backdrop-blur-sm"
        @click.self="closeCondoModal"
      >
        <transition
          enter-active-class="transition duration-300 ease-out"
          enter-from-class="opacity-0 scale-95 translate-y-4"
          enter-to-class="opacity-100 scale-100 translate-y-0"
          leave-active-class="transition duration-200 ease-in"
          leave-from-class="opacity-100 scale-100 translate-y-0"
          leave-to-class="opacity-0 scale-95 translate-y-4"
        >
          <div 
            v-if="showCondoModal" 
            class="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden my-8"
          >
            <!-- Top accent line -->
            <div class="h-2 bg-gradient-to-r from-emerald-400 via-teal-500 to-emerald-600" />

            <!-- Header -->
            <div class="p-6 sm:p-8 pb-3 flex items-start justify-between">
              <div>
                <div class="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-50 border border-emerald-100 rounded-full text-xs font-semibold text-emerald-600 uppercase tracking-wider mb-2">
                  <i class="fas fa-calculator text-2xs" /> Cotizador Condominios
                </div>
                <h3 class="text-2xl font-bold text-gray-900 font-display">Calcula tu plan mensual</h3>
                <p class="text-xs text-gray-500 mt-1">Transparente, sin cargos ocultos ni comisiones extra.</p>
              </div>
              <button 
                type="button"
                class="w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors" 
                aria-label="Cerrar modal"
                @click="closeCondoModal"
              >
                <i class="fas fa-times text-sm" />
              </button>
            </div>

            <!-- Body -->
            <div class="p-6 sm:p-8 pt-2 space-y-5">
              <!-- Field: Nombre -->
              <div>
                <label class="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">
                  Tu Nombre o Administradora <span class="text-emerald-500">*</span>
                </label>
                <div class="relative">
                  <div class="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400">
                    <i class="fas fa-user text-sm" />
                  </div>
                  <input 
                    v-model="condoQuote.name" 
                    type="text" 
                    placeholder="Ej. Carlos Mendoza / Admin Inmuebles C.A."
                    class="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:bg-white transition-all"
                    :class="{'border-red-400 ring-2 ring-red-100': condoQuoteError}"
                    @input="condoQuoteError = ''"
                  />
                </div>
                <p v-if="condoQuoteError" class="text-xs text-red-500 mt-1.5 flex items-center gap-1">
                  <i class="fas fa-exclamation-circle text-xs" /> {{ condoQuoteError }}
                </p>
              </div>

              <!-- Field: Cantidad de edificios -->
              <div>
                <div class="flex items-center justify-between mb-2">
                  <label class="text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Cantidad de edificios a gestionar
                  </label>
                  <span class="text-xs font-bold text-emerald-600 bg-emerald-50 px-2.5 py-0.5 rounded-full border border-emerald-100">
                    {{ condoQuote.buildings }} {{ condoQuote.buildings === 1 ? 'edificio' : 'edificios' }}
                  </span>
                </div>

                <!-- Input with Steppers -->
                <div class="flex items-center gap-3 mb-3">
                  <button 
                    type="button"
                    class="w-11 h-11 rounded-xl bg-gray-100 hover:bg-gray-200 active:scale-95 text-gray-700 font-bold flex items-center justify-center transition-all cursor-pointer"
                    title="Restar 1 edificio"
                    @click="decrementBuildings(1)"
                  >
                    <i class="fas fa-minus text-xs" />
                  </button>
                  <div class="relative flex-1">
                    <input 
                      v-model.number="condoQuote.buildings" 
                      type="number" 
                      min="1"
                      class="w-full text-center py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-base font-bold text-gray-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:bg-white transition-all"
                    />
                  </div>
                  <button 
                    type="button"
                    class="w-11 h-11 rounded-xl bg-gray-100 hover:bg-gray-200 active:scale-95 text-gray-700 font-bold flex items-center justify-center transition-all cursor-pointer"
                    title="Sumar 1 edificio"
                    @click="incrementBuildings(1)"
                  >
                    <i class="fas fa-plus text-xs" />
                  </button>
                </div>

                <!-- Range Slider -->
                <div class="px-1">
                  <input 
                    v-model.number="condoQuote.buildings" 
                    type="range" 
                    min="1" 
                    max="150" 
                    class="w-full accent-emerald-500 cursor-pointer"
                  />
                </div>

                <!-- Quick presets -->
                <div class="flex flex-wrap gap-1.5 mt-2.5">
                  <button 
                    v-for="preset in [5, 10, 25, 50, 100]" 
                    :key="preset"
                    type="button"
                    class="px-2.5 py-1 text-2xs font-medium rounded-lg border transition-all cursor-pointer"
                    :class="condoQuote.buildings === preset ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'"
                    @click="setBuildingsPreset(preset)"
                  >
                    {{ preset }} edif.
                  </button>
                </div>
              </div>

              <!-- Price breakdown card -->
              <div class="p-5 bg-gradient-to-br from-emerald-50 via-teal-50/40 to-white border border-emerald-100 rounded-2xl">
                <!-- Tariff rule notes -->
                <div class="flex items-center justify-between text-xs text-gray-600 pb-2.5 border-b border-emerald-100">
                  <span class="flex items-center gap-1.5 font-medium">
                    <i class="fas fa-tag text-emerald-500 text-xs" /> Tarifa por edificio:
                  </span>
                  <span class="font-bold text-gray-900">0,70$ USD / mes</span>
                </div>
                <div class="flex items-center justify-between text-xs text-gray-600 py-2 border-b border-emerald-100">
                  <span class="flex items-center gap-1.5 font-medium">
                    <i class="fas fa-shield-alt text-emerald-500 text-xs" /> Tarifa mínima mensual:
                  </span>
                  <span class="font-bold text-gray-900">30,00$ USD / mes</span>
                </div>

                <!-- Subtotal vs Minimum explanation -->
                <div class="py-2.5 text-xs">
                  <div v-if="isCondoMinimumApplied" class="text-emerald-800 bg-emerald-100 bg-opacity-70 p-2.5 rounded-xl border border-emerald-200 border-opacity-60">
                    <div class="font-semibold flex items-center gap-1">
                      <i class="fas fa-info-circle text-emerald-600" /> Aplica tarifa mínima mensual garantizada ($30/mes)
                    </div>
                    <div class="text-2xs text-emerald-700 mt-0.5">
                      {{ condoQuote.buildings }} × 0,70$ = {{ computedCondoCalculated }}$ (la tarifa mínima de $30 cubre hasta 42 edificios).
                    </div>
                  </div>
                  <div v-else class="text-gray-600 flex items-center justify-between">
                    <span>Desglose de cálculo:</span>
                    <span class="font-semibold text-gray-800">{{ condoQuote.buildings }} edificios × 0,70$</span>
                  </div>
                </div>

                <!-- Total highlight -->
                <div class="pt-3 mt-1 border-t border-emerald-200 border-opacity-60 flex items-baseline justify-between">
                  <span class="text-xs font-bold text-gray-700 uppercase tracking-wider">Total mensual estimado:</span>
                  <div class="text-right">
                    <span class="text-3xl font-extrabold text-emerald-600 font-display">${{ computedCondoTotal }}</span>
                    <span class="text-xs text-gray-500 font-medium ml-1">USD / mes</span>
                  </div>
                </div>
              </div>

              <!-- WhatsApp CTA Button -->
              <button 
                type="button"
                class="w-full flex items-center justify-center gap-3 py-4 px-6 bg-green-500 hover:bg-green-600 active:scale-95 text-white font-bold text-sm sm:text-base rounded-2xl shadow-lg shadow-green transition-all duration-300 cursor-pointer" 
                @click="sendCondoWhatsApp"
              >
                <i class="fab fa-whatsapp text-xl" />
                Preguntar por WhatsApp con esta cotización
              </button>

              <p class="text-center text-2xs text-gray-400">
                Al hacer clic, se abrirá WhatsApp con el nombre de cliente y la cotización para {{ condoQuote.buildings }} edificios lista para enviar.
              </p>
            </div>
          </div>
        </transition>
      </div>
    </transition>
  </WebLayout>
</template>

<script>
import { Link, Head } from '@inertiajs/inertia-vue3'
import WebLayout from '@/Layouts/WebLayout.vue'

export default {
  components: {
    Link,
    Head,
    WebLayout,
  },
  props: {
    solutions: { type: Array, default: () => [] },
    adminFeatures: { type: Array, default: () => [] },
    condoFeatures: { type: Array, default: () => [] },
    ecommerceFeatures: { type: Array, default: () => [] },
    stats: { type: Array, default: () => [] },
    testimonials: { type: Array, default: () => [] },
  },
  data() {
    return {
      activeAdminTab: 0,
      showCondoModal: false,
      condoQuote: {
        name: '',
        buildings: 25,
      },
      condoQuoteError: '',
      ecommerceSteps: [
        'Carga tus productos',
        'Comparte el enlace',
        'Recibe pedidos por WhatsApp',
      ],
    }
  },
  computed: {
    computedCondoCalculated() {
      const count = parseInt(this.condoQuote.buildings) || 0
      return (count * 0.70).toFixed(2)
    },
    computedCondoTotal() {
      const count = parseInt(this.condoQuote.buildings) || 0
      const total = Math.max(30, count * 0.70)
      return total.toFixed(2)
    },
    isCondoMinimumApplied() {
      const count = parseInt(this.condoQuote.buildings) || 0
      return (count * 0.70) < 30
    },
  },
  methods: {
    openCondoModal() {
      this.showCondoModal = true
      this.condoQuoteError = ''
    },
    closeCondoModal() {
      this.showCondoModal = false
      this.condoQuoteError = ''
    },
    incrementBuildings(amount = 1) {
      const current = parseInt(this.condoQuote.buildings) || 0
      this.condoQuote.buildings = current + amount
    },
    decrementBuildings(amount = 1) {
      const current = parseInt(this.condoQuote.buildings) || 0
      this.condoQuote.buildings = Math.max(1, current - amount)
    },
    setBuildingsPreset(n) {
      this.condoQuote.buildings = n
    },
    sendCondoWhatsApp() {
      if (!this.condoQuote.name || !this.condoQuote.name.trim()) {
        this.condoQuoteError = 'Por favor ingresa tu nombre o el de tu administradora.'
        return
      }
      this.condoQuoteError = ''
      const name = this.condoQuote.name.trim()
      const count = parseInt(this.condoQuote.buildings) || 1
      const total = this.computedCondoTotal
      const minNote = this.isCondoMinimumApplied ? ' (tarifa mínima mensual)' : ''

      const message = `¡Hola! Mi nombre es ${name}. Me interesa implementar Realgestión Condominios para ${count} ${count === 1 ? 'edificio' : 'edificios'}.\n\nSegún la cotización en su web, la tarifa estimada es de $${total} USD/mes${minNote}.\n\n¿Podrían darme más información y coordinar una demostración?`

      const url = `https://wa.me/584244010195?text=${encodeURIComponent(message)}`
      window.open(url, '_blank')
    },
  },
}
</script>

<style>
/* ── Fonts ── */
.font-display {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* ── TW2 Compatibility: Custom classes for features unavailable in Tailwind 2 ── */
.bg-warm-cream { background-color: #FAFAF7; }
.hero-section { padding-top: 72px; }
.hero-headline { line-height: 1.08; }

.gradient-text {
  background: linear-gradient(to right, #f97316, #f43f5e, #f97316);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* Background orbs */
.orb-1 { top: 10%; left: 5%; width: 500px; height: 500px; background: rgba(251, 191, 36, 0.12); filter: blur(120px); }
.orb-2 { bottom: 10%; right: 10%; width: 400px; height: 400px; background: rgba(245, 158, 11, 0.12); filter: blur(100px); }
.orb-3 { top: 40%; right: 30%; width: 300px; height: 300px; background: rgba(244, 63, 94, 0.06); filter: blur(80px); }

/* Dot patterns */
.dot-pattern {
  background-image: radial-gradient(circle, rgba(0,0,0,0.03) 1px, transparent 1px);
  background-size: 24px 24px;
  -webkit-mask-image: radial-gradient(ellipse at center, black 30%, transparent 70%);
  mask-image: radial-gradient(ellipse at center, black 30%, transparent 70%);
}
.dot-pattern-light {
  background-image: radial-gradient(circle, rgba(255,255,255,0.03) 1px, transparent 1px);
  background-size: 16px 16px;
}
.dot-pattern-dark {
  background-image: radial-gradient(circle, rgba(255,255,255,0.04) 1px, transparent 1px);
  background-size: 20px 20px;
}

/* Radial glows */
.radial-orange-glow {
  background: radial-gradient(ellipse at center, rgba(249,115,22,0.05), transparent 60%);
}
.radial-orange-accent {
  background: radial-gradient(circle at 70% 20%, rgba(249,115,22,0.15), transparent 50%);
}
.radial-blue-glow {
  background: radial-gradient(circle at 30% 50%, rgba(59,130,246,0.15), transparent 50%);
}
.radial-orange-ecom {
  background: radial-gradient(circle at 70% 80%, rgba(249,115,22,0.1), transparent 50%);
}

/* Backdrop blur fallback */
.backdrop-blur-sm { -webkit-backdrop-filter: blur(4px); backdrop-filter: blur(4px); }
.backdrop-blur-xl { -webkit-backdrop-filter: blur(24px); backdrop-filter: blur(24px); }

/* Shadow with color (Tailwind 2 doesn't support shadow-color) */
.shadow-orange { box-shadow: 0 20px 40px -12px rgba(249,115,22,0.2); }
.shadow-green { box-shadow: 0 20px 40px -12px rgba(16,185,129,0.2); }

/* Custom micro-typography */
.text-2xs { font-size: 10.5px; line-height: 14px; }

/* Phone mockup utilities */
.phone-mockup-wrapper { max-width: 280px; }
.phone-aspect { aspect-ratio: 9/18; }

/* Card hover shadows */
.card-hover-shadow { transition: all 0.5s ease; }
.card-hover-shadow:hover { box-shadow: 0 20px 60px -12px rgba(0,0,0,0.1); }
.card-hover-shadow-emerald { transition: all 0.5s ease; }
.card-hover-shadow-emerald:hover { box-shadow: 0 20px 60px -12px rgba(16,185,129,0.14); }

/* Quote decoration */
.quote-icon-opacity { opacity: 0.04; transition: opacity 0.3s; }
.group:hover .quote-icon-opacity { opacity: 0.08; }

/* Floating pills positioning across the Hero */
.pill-pos-1 { top: 18%; left: 3%; }
.pill-pos-2 { top: 20%; right: 3%; }
.pill-pos-3 { top: 48%; left: 1.5%; }
.pill-pos-4 { top: 48%; right: 1.5%; }
.pill-pos-5 { top: 76%; left: 4%; }
.pill-pos-6 { top: 77%; right: 4%; }

@media (min-width: 1280px) {
  .pill-pos-1 { top: 18%; left: 5%; }
  .pill-pos-2 { top: 20%; right: 5%; }
  .pill-pos-3 { top: 48%; left: 2.5%; }
  .pill-pos-4 { top: 48%; right: 2.5%; }
  .pill-pos-5 { top: 76%; left: 6%; }
  .pill-pos-6 { top: 77%; right: 6%; }
}

@media (min-width: 1536px) {
  .pill-pos-1 { top: 20%; left: 8%; }
  .pill-pos-2 { top: 22%; right: 8%; }
  .pill-pos-3 { top: 48%; left: 5%; }
  .pill-pos-4 { top: 48%; right: 5%; }
  .pill-pos-5 { top: 76%; left: 9%; }
  .pill-pos-6 { top: 77%; right: 9%; }
}

/* ── Animations ── */
@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes floatSlow {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(20px, -30px); }
}

@keyframes floatSlower {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(-15px, 20px); }
}

@keyframes scrollDown {
  0% { transform: translateY(0); opacity: 1; }
  50% { transform: translateY(6px); opacity: 0.5; }
  100% { transform: translateY(0); opacity: 1; }
}

@keyframes floatPill {
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  33% { transform: translateY(-10px) rotate(1deg); }
  66% { transform: translateY(6px) rotate(-1deg); }
}

.animate-fade-in-up {
  animation: fadeInUp 0.7s ease-out forwards;
  opacity: 0;
}

.animation-delay-100 { animation-delay: 0.1s; }
.animation-delay-200 { animation-delay: 0.2s; }
.animation-delay-300 { animation-delay: 0.3s; }
.animation-delay-400 { animation-delay: 0.4s; }

.animate-float-slow { animation: floatSlow 8s ease-in-out infinite; }
.animate-float-slower { animation: floatSlower 10s ease-in-out infinite; }
.animate-scroll-down { animation: scrollDown 1.5s ease-in-out infinite; }

.floating-pill { animation: floatPill 6s ease-in-out infinite; }
.pill-delay-1 { animation-delay: 0s; }
.pill-delay-2 { animation-delay: 1.2s; }
.pill-delay-3 { animation-delay: 2.4s; }
.pill-delay-4 { animation-delay: 3.6s; }
.pill-delay-5 { animation-delay: 4.8s; }
.pill-delay-6 { animation-delay: 1.8s; }

/* ── Feature tab transition ── */
.feature-fade-enter-active {
  transition: all 0.3s ease-out;
}
.feature-fade-leave-active {
  transition: all 0.2s ease-in;
}
.feature-fade-enter-from {
  opacity: 0;
  transform: translateY(12px);
}
.feature-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* ── Smooth scroll ── */
html {
  scroll-behavior: smooth;
}
</style>